import {FileUpload} from '@/components/atoms';
import PropTypes from 'prop-types';
import {Controller} from 'react-hook-form';
import {useIntl} from 'react-intl';
import {
  uploadTierImage,
  readFileAsDataUrl,
  cacheImageDataUrl,
  getCachedImageDataUrl,
  convertBase64ToDataUrl
} from '@/services/tierImageService';
import {useState, useEffect, useCallback} from 'react';
import {useErrorContext} from '@/context/Error/useErrorContext';
import {logger} from '@/helpers/logger';

const ACCEPTED_IMAGE_EXTENSIONS = ['image/jpeg', 'image/png', 'image/webp'];

const extractFileFromInput = (files) => {
  if (files instanceof File) {
    return files;
  }
  if (files?.length > 0) {
    return files[0];
  }
  return null;
};

export const ImageUploader = ({
  control,
  name,
  label,
  helperText,
  initialImageData,
  initialImageUrl,
  useBlobUpload = true
}) => {
  const {formatMessage} = useIntl();
  const {showError} = useErrorContext();
  const [imageUri, setImageUri] = useState('');

  useEffect(() => {
    if (useBlobUpload && initialImageUrl) {
      setImageUri(initialImageUrl);
    } else if (!useBlobUpload && initialImageData) {
      const dataUrl = convertBase64ToDataUrl(initialImageData);
      if (dataUrl) {
        setImageUri(dataUrl);
      }
    }
  }, [initialImageUrl, initialImageData, useBlobUpload]);

  const handleImageUpload = useCallback(
    async (files) => {
      const file = extractFileFromInput(files);

      if (!file) {
        return null;
      }

      if (!ACCEPTED_IMAGE_EXTENSIONS.includes(file.type)) {
        const errorMessage = `Unsupported image format. Supported formats: JPEG, PNG, WebP`;
        showError(errorMessage);
        throw new Error(errorMessage);
      }

      try {
        const response = await uploadTierImage(file, useBlobUpload);

        if (useBlobUpload) {
          const cdnUrl = response?.cdnUrl;
          const fileName = response?.fileName;
          setImageUri(cdnUrl);
          cacheImageDataUrl(cdnUrl, cdnUrl, fileName);
          return cdnUrl;
        } else {
          const {imageUploadedRef, optimizedImage} = response;
          const dataUrl = await readFileAsDataUrl(optimizedImage);
          setImageUri(dataUrl);
          cacheImageDataUrl(imageUploadedRef, dataUrl, file.name);
          return imageUploadedRef;
        }
      } catch (error) {
        setImageUri('');
        showError(formatMessage({id: 'CLUB_PLUS.IMAGE_UPLOAD_FAILED'}));
        throw error;
      }
    },
    [showError, formatMessage, useBlobUpload]
  );

  const handleImageDelete = useCallback(() => {
    setImageUri('');
  }, []);

  return (
    <div className='h-fit rounded-lg border border-[color:var(--color-border-default)] p-6'>
      <Controller
        name={name}
        control={control}
        render={({field}) => {
          const displayUri = field.value
            ? getCachedImageDataUrl(field.value)
            : imageUri;

          const onUpload = async (files) => {
            try {
              const result = await handleImageUpload(files);
              field.onChange(result);
            } catch (error) {
              logger.error('Error in onUpload callback:', error);
            }
          };

          const onUploadDelete = () => {
            handleImageDelete();
            field.onChange(useBlobUpload ? '' : 0);
          };

          return (
            <FileUpload
              className=''
              description={formatMessage({
                id: 'CLUB_PLUS.TIER_CREATE_IMAGE_UPLOADER'
              })}
              feedbackType='info'
              label={label}
              type='image'
              variant='default'
              imageUri={displayUri}
              onUpload={onUpload}
              onUploadDelete={onUploadDelete}
              helperText={helperText}
            />
          );
        }}
      />
    </div>
  );
};

ImageUploader.propTypes = {
  control: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  helperText: PropTypes.string,
  initialImageData: PropTypes.string,
  initialImageUrl: PropTypes.string,
  useBlobUpload: PropTypes.bool
};
